/**
 * The object tree of one container, loaded a level at a time. Shows structure only: name,
 * type and the restricted flag (design 16.5: the console configures structure and access,
 * not project content). Template containers may show more through `extra`.
 */
import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { WorkObject, type DefinitionDetail } from "@workcore/schemas";

/** a page of whole objects (no `fields`) */
const FullPage = z.object({ items: z.array(WorkObject), nextCursor: z.number().int().nullable() });
import { get } from "../../api";
import { ErrorBox } from "../../ui";

export const treeKey = (slug: string, containerId: number) => ["ws", slug, "tree", containerId];

function useLevel(slug: string, containerId: number, parentId: number | null) {
  return useQuery({
    queryKey: [...treeKey(slug, containerId), parentId ?? "root"],
    queryFn: async () => {
      if (parentId !== null) return z.array(WorkObject).parse(await get(`/v1/ws/${slug}/objects/${parentId}/children`));
      // Roots, all pages (structure trees are small; 200 per page).
      const out: WorkObject[] = [];
      let cursor: number | undefined;
      do {
        const page = FullPage.parse(await get(`/v1/ws/${slug}/objects`, { container_id: containerId, root: "true", limit: 200, cursor }));
        out.push(...page.items);
        cursor = page.nextCursor ?? undefined;
      } while (cursor);
      return out;
    },
  });
}

interface TreeProps {
  slug: string;
  containerId: number;
  def: DefinitionDetail | undefined;
  selectedId?: number | undefined;
  onSelect?: (o: WorkObject) => void;
  /** extra content after a node's label */
  extra?: (o: WorkObject) => ReactNode;
}

function Level({ parentId, depth, ...p }: TreeProps & { parentId: number | null; depth: number }) {
  const level = useLevel(p.slug, p.containerId, parentId);
  if (level.error) return <ErrorBox error={level.error} />;
  if (level.isLoading) return <p className="muted small">Loading…</p>;
  const items = [...(level.data ?? [])].sort((a, b) => a.sequence - b.sequence || a.id - b.id);
  if (items.length === 0 && depth === 0) return <p className="muted">Nothing here yet.</p>;
  return (
    <ul className="tree objects">
      {items.map((o) => <Node key={o.id} o={o} depth={depth} {...p} />)}
    </ul>
  );
}

function Node({ o, depth, ...p }: TreeProps & { o: WorkObject; depth: number }) {
  const [open, setOpen] = useState(depth < 1);
  const type = p.def?.types.find((t) => t.id === o.typeId);
  return (
    <li>
      <button className="link toggle" aria-label={open ? `Collapse ${o.name}` : `Expand ${o.name}`} onClick={() => setOpen(!open)}>
        {open ? "▾" : "▸"}
      </button>
      <button className={`node ${p.selectedId === o.id ? "selected" : ""}`} onClick={() => p.onSelect?.(o)}>
        <span className="muted small">{type?.name ?? "?"}</span> {o.name} <span className="muted small">#{o.id}</span>
      </button>
      {o.restricted && <span className="badge badge-restricted" title="Inheritance stops here: only grants on this node and below apply">restricted</span>}
      {p.extra?.(o)}
      {open && <Level parentId={o.id} depth={depth + 1} {...p} />}
    </li>
  );
}

export function ObjectTree(p: TreeProps) {
  return <Level parentId={null} depth={0} {...p} />;
}
